import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SubscriptionManager from '../components/SubscriptionManager';
import UserManagement from '../components/UserManagement';

import SelectMenu from "../components/crm/SelectMenu";
const AdminFunctions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('App Update Available');
  const [notificationMessage, setNotificationMessage] = useState(
    'A new version of Jurident is available. Please update your app for the best experience.'
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUserFailures, setFilteredUserFailures] = useState([]);
  const [filteredUsersWithoutTokens, setFilteredUsersWithoutTokens] = useState([]);
  const [filteredUsersWithExpiredTokens, setFilteredUsersWithExpiredTokens] = useState([]);
  const [filteredUsersWithExcessiveTokens, setFilteredUsersWithExcessiveTokens] = useState([]);

  // Razorpay transactions state
  const [razorpayTransactions, setRazorpayTransactions] = useState([]);
  const [razorpayStats, setRazorpayStats] = useState(null);
  const [razorpayPagination, setRazorpayPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 1 });
  const [isLoadingRazorpay, setIsLoadingRazorpay] = useState(false);
  const [razorpayStatusFilter, setRazorpayStatusFilter] = useState('all');
  const [razorpaySearchQuery, setRazorpaySearchQuery] = useState('');
  const [filteredRazorpayTransactions, setFilteredRazorpayTransactions] = useState([]);

  const functions = getFunctions();

  // Fetch analytics on component mount and when timeRange changes
  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  // Filter user failures when search query or analytics changes
  useEffect(() => {
    if (analytics && analytics.userFailures) {
      if (searchQuery.trim() === '') {
        setFilteredUserFailures(analytics.userFailures);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = analytics.userFailures.filter((userFailure) => {
          const name = userFailure.user.name?.toLowerCase() || '';
          const email = userFailure.user.email?.toLowerCase() || '';
          const userId = userFailure.user.userId?.toLowerCase() || '';
          return name.includes(query) || email.includes(query) || userId.includes(query);
        });
        setFilteredUserFailures(filtered);
      }
    }
  }, [searchQuery, analytics]);

  // Filter users without tokens when search query or analytics changes
  useEffect(() => {
    if (analytics && analytics.usersWithoutTokens) {
      if (searchQuery.trim() === '') {
        setFilteredUsersWithoutTokens(analytics.usersWithoutTokens);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = analytics.usersWithoutTokens.filter((user) => {
          const name = user.name?.toLowerCase() || '';
          const email = user.email?.toLowerCase() || '';
          const userId = user.userId?.toLowerCase() || '';
          const phone = user.mobileNumber?.toLowerCase() || '';
          return name.includes(query) || email.includes(query) || userId.includes(query) || phone.includes(query);
        });
        setFilteredUsersWithoutTokens(filtered);
      }
    }
  }, [searchQuery, analytics]);

  // Filter users with expired tokens
  useEffect(() => {
    if (analytics && analytics.usersWithExpiredTokens) {
      if (searchQuery.trim() === '') {
        setFilteredUsersWithExpiredTokens(analytics.usersWithExpiredTokens);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = analytics.usersWithExpiredTokens.filter((user) => {
          const name = user.name?.toLowerCase() || '';
          const email = user.email?.toLowerCase() || '';
          const userId = user.userId?.toLowerCase() || '';
          return name.includes(query) || email.includes(query) || userId.includes(query);
        });
        setFilteredUsersWithExpiredTokens(filtered);
      }
    }
  }, [searchQuery, analytics]);

  // Filter users with excessive tokens
  useEffect(() => {
    if (analytics && analytics.usersWithExcessiveTokens) {
      if (searchQuery.trim() === '') {
        setFilteredUsersWithExcessiveTokens(analytics.usersWithExcessiveTokens);
      } else {
        const query = searchQuery.toLowerCase();
        const filtered = analytics.usersWithExcessiveTokens.filter((user) => {
          const name = user.name?.toLowerCase() || '';
          const email = user.email?.toLowerCase() || '';
          const userId = user.userId?.toLowerCase() || '';
          return name.includes(query) || email.includes(query) || userId.includes(query);
        });
        setFilteredUsersWithExcessiveTokens(filtered);
      }
    }
  }, [searchQuery, analytics]);

  // Filter Razorpay transactions when search query changes
  useEffect(() => {
    if (razorpayTransactions.length > 0) {
      if (razorpaySearchQuery.trim() === '') {
        setFilteredRazorpayTransactions(razorpayTransactions);
      } else {
        const query = razorpaySearchQuery.toLowerCase();
        const filtered = razorpayTransactions.filter((tx) => {
          const name = tx.user?.name?.toLowerCase() || '';
          const email = tx.user?.email?.toLowerCase() || '';
          const orderId = tx.orderId?.toLowerCase() || '';
          const paymentId = tx.paymentId?.toLowerCase() || '';
          return name.includes(query) || email.includes(query) || orderId.includes(query) || paymentId.includes(query);
        });
        setFilteredRazorpayTransactions(filtered);
      }
    } else {
      setFilteredRazorpayTransactions([]);
    }
  }, [razorpaySearchQuery, razorpayTransactions]);

  // Fetch Razorpay transactions when tab is active
  useEffect(() => {
    if (activeTab === 'razorpay') {
      fetchRazorpayTransactions();
    }
  }, [activeTab, razorpayStatusFilter, razorpayPagination.page]);

  const fetchRazorpayTransactions = async () => {
    setIsLoadingRazorpay(true);
    try {
      const getRazorpayTransactionsFunction = httpsCallable(functions, 'getRazorpayTransactions');
      const result = await getRazorpayTransactionsFunction({
        page: razorpayPagination.page,
        pageSize: razorpayPagination.pageSize,
        status: razorpayStatusFilter,
      });
      setRazorpayTransactions(result.data.transactions || []);
      setFilteredRazorpayTransactions(result.data.transactions || []);
      setRazorpayStats(result.data.stats);
      setRazorpayPagination(result.data.pagination);
    } catch (error) {
      console.error('Error fetching Razorpay transactions:', error);
      toast.error('Failed to fetch Razorpay transactions');
    } finally {
      setIsLoadingRazorpay(false);
    }
  };

  const fetchAnalytics = async () => {
    setIsLoadingAnalytics(true);
    try {
      const getAnalyticsFunction = httpsCallable(functions, 'getNotificationAnalytics');
      const result = await getAnalyticsFunction({ timeRange });
      setAnalytics(result.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics data');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleClearPastHearingDates = async () => {
    setIsLoading(true);
    setResultMessage('');

    try {
      const clearPastHearingDatesFunction = httpsCallable(functions, 'clearPastHearingDates');
      const result = await clearPastHearingDatesFunction();

      const data = result.data;

      let resultText = `✅ ${data.message}\n\n`;
      resultText += `📊 Statistics:\n`;
      resultText += `• Total Cleared: ${data.totalCleared}\n`;
      resultText += `• Errors: ${data.totalErrors}\n\n`;

      if (data.casesProcessed && data.casesProcessed.length > 0) {
        resultText += `📝 Cases Processed:\n`;
        data.casesProcessed.forEach((caseInfo) => {
          resultText += `  • Case: ${caseInfo.caseNo} (${caseInfo.collection}) - Cleared date: ${caseInfo.clearedDate}\n`;
        });
      } else {
        resultText += `📝 No cases were processed\n`;
      }

      setResultMessage(resultText);
      toast.success('Past hearing dates cleared successfully!', {
        position: 'top-right',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error clearing past hearing dates:', error);
      const errorMsg = `❌ Error: ${error.message}`;
      setResultMessage(errorMsg);
      toast.error(`Failed to clear past hearing dates: ${error.message}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleSendAppUpdateNotification = async () => {
    setIsLoading(true);
    setResultMessage('');

    try {
      const sendAppUpdateNotificationFunction = httpsCallable(functions, 'sendAppUpdateNotification');
      const result = await sendAppUpdateNotificationFunction({
        title: notificationTitle,
        message: notificationMessage,
      });

      const data = result.data;

      let resultText = `✅ ${data.message}\n\n`;
      resultText += `📊 Statistics:\n`;
      resultText += `• Total Users: ${data.totalUsers}\n`;
      resultText += `• Notifications Sent: ${data.totalNotificationsSent}\n`;
      resultText += `• Failed: ${data.totalNotificationsFailed}\n`;
      resultText += `• Users Without Tokens: ${data.usersWithoutTokens}\n`;

      setResultMessage(resultText);
      toast.success('App update notifications sent successfully!', {
        position: 'top-right',
        autoClose: 5000,
      });

      // Refresh analytics after sending notifications//
      fetchAnalytics();
    } catch (error) {
      console.error('Error sending app update notification:', error);
      const errorMsg = `❌ Error: ${error.message}`;
      setResultMessage(errorMsg);
      toast.error(`Failed to send notifications: ${error.message}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleExtendFreeSubscriptions = async () => {
    setIsLoading(true);
    setResultMessage('');

    try {
      const extendFreeSubscriptionsFunction = httpsCallable(functions, 'extendFreeSubscriptions');
      const result = await extendFreeSubscriptionsFunction({
        daysToExtend: 7, // Extend by 1 week (7 days)
      });

      const data = result.data;

      let resultText = `✅ ${data.message}\n\n`;
      resultText += `📊 Statistics:\n`;
      resultText += `• Days Extended: ${data.daysExtended}\n`;
      resultText += `• Subscriptions Extended: ${data.totalSubscriptionsExtended}\n`;
      resultText += `• Failed Extensions: ${data.totalSubscriptionsFailed}\n`;
      resultText += `• Total Users: ${data.totalUsers}\n`;
      resultText += `• Notifications Sent: ${data.totalNotificationsSent}\n`;
      resultText += `• Failed Notifications: ${data.totalNotificationsFailed}\n`;
      resultText += `• Users Without Tokens: ${data.usersWithoutTokens}\n`;

      setResultMessage(resultText);
      toast.success('Free subscriptions extended successfully!', {
        position: 'top-right',
        autoClose: 5000,
      });

      // Refresh analytics after extending subscriptions
      fetchAnalytics();
    } catch (error) {
      console.error('Error extending free subscriptions:', error);
      const errorMsg = `❌ Error: ${error.message}`;
      setResultMessage(errorMsg);
      toast.error(`Failed to extend subscriptions: ${error.message}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      setShowConfirmModal(false);
    }
  };

  const openConfirmModal = (action) => {
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'sent':
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-900 to-indigo-900">
      <Navbar />
      <ToastContainer />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-4">
            🛠️ Admin Dashboard
          </h1>
          <p className="text-gray-300 text-lg">
            Comprehensive notification analytics and system management
          </p>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-gray-800">Processing... Please wait</p>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Action</h3>
              <p className="text-gray-700 mb-6">
                Are you sure you want to proceed with this action? This operation will affect multiple users/cases.
              </p>
              <div className="flex gap-4 justify-end">
                <button
                  onClick={closeConfirmModal}
                  className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State for Initial Load */}
        {isLoadingAnalytics && !analytics && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <p className="mt-4 text-lg font-medium text-white">Loading Analytics Dashboard...</p>
            <p className="mt-2 text-sm text-gray-300">Fetching notification statistics and user data</p>
          </div>
        )}

        {/* Analytics Summary Cards */}
        {analytics && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-white">📊 Analytics Overview</h2>
              <div className="flex gap-4 items-center">
                <SelectMenu
                  value={timeRange}
                  onChange={(e) => setTimeRange(Number(e.target.value))}
                  className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>Last 24 hours</option>
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </SelectMenu>
                <button
                  onClick={fetchAnalytics}
                  disabled={isLoadingAnalytics}
                  className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Total Users Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold opacity-90">Total Users</h3>
                  <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-4xl font-bold">{analytics.summary.totalUsers}</p>
                <p className="text-sm opacity-80 mt-2">
                  {analytics.summary.usersWithoutTokensCount} without FCM tokens
                </p>
              </div>

              {/* Success Rate Card */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold opacity-90">Success Rate</h3>
                  <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-4xl font-bold">{analytics.summary.successRate}%</p>
                <p className="text-sm opacity-80 mt-2">
                  {analytics.summary.totalSuccess} / {analytics.summary.totalSuccess + analytics.summary.totalFailure} notifications
                </p>
              </div>

              {/* Event Reminders Card */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold opacity-90">Event Reminders</h3>
                  <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-4xl font-bold">{analytics.summary.totalReminders}</p>
                <p className="text-sm opacity-80 mt-2">
                  {analytics.remindersAnalytics.sent} sent, {analytics.remindersAnalytics.scheduled} scheduled
                </p>
              </div>

              {/* Failed Notifications Card */}
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold opacity-90">Failed</h3>
                  <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-4xl font-bold">{analytics.summary.totalFailure}</p>
                <p className="text-sm opacity-80 mt-2">
                  {Object.keys(analytics.userFailures).length} users affected
                </p>
              </div>

              {/* Expired Tokens Card */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold opacity-90">Expired Tokens</h3>
                  <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-4xl font-bold">{analytics.summary.expiredTokenCount || 0}</p>
                <p className="text-sm opacity-80 mt-2">
                  {analytics.summary.usersWithExpiredTokensCount || 0} users with expired tokens
                </p>
              </div>

              {/* Excessive Tokens Card */}
              <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold opacity-90">Excessive Tokens</h3>
                  <svg className="w-8 h-8 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <p className="text-4xl font-bold">{analytics.summary.usersWithExcessiveTokensCount || 0}</p>
                <p className="text-sm opacity-80 mt-2">
                  Users with &gt;5 tokens
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px overflow-x-auto">
                  {[
                    { id: 'overview', label: 'Overview', icon: '📊' },
                    { id: 'user-management', label: 'User Management', icon: '👤' },
                    { id: 'reminders', label: 'Event Reminders', icon: '🔔' },
                    { id: 'failures', label: 'User Failures', icon: '❌' },
                    { id: 'users', label: 'Users Without Tokens', icon: '👥' },
                    { id: 'expired', label: 'Expired Tokens', icon: '⏰' },
                    { id: 'excessive', label: 'Excessive Tokens', icon: '⚠️' },
                    { id: 'subscriptions', label: 'Subscriptions', icon: '💳' },
                    { id: 'razorpay', label: 'Razorpay Transactions', icon: '💰' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-4 font-semibold text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'border-b-2 border-indigo-600 text-indigo-600'
                          : 'text-gray-600 hover:text-gray-800 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Notification Types Breakdown</h3>
                    {Object.keys(analytics.logsByType).length > 0 ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {Object.entries(analytics.logsByType).map(([type, data]) => (
                          <div key={type} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                              <h4 className="text-lg font-bold text-gray-900 capitalize">
                                {type.replace(/_/g, ' ')}
                              </h4>
                              <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-semibold">
                                {data.total} total
                              </span>
                            </div>
                            <div className="space-y-3">
                              <div className="flex justify-between">
                                <span className="text-gray-600">✅ Successful:</span>
                                <span className="font-semibold text-green-600">{data.success}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">❌ Failed:</span>
                                <span className="font-semibold text-red-600">{data.failed}</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
                                <div
                                  className="bg-green-500 h-3 rounded-full transition-all"
                                  style={{ width: `${data.total > 0 ? (data.success / data.total) * 100 : 0}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg">No notification logs available for the selected time range</p>
                      </div>
                    )}
                  </div>
                )}

                {/* User Management Tab */}
                {activeTab === 'user-management' && (
                  <div className="space-y-6">
                    <UserManagement />
                  </div>
                )}

                {/* Event Reminders Tab */}
                {activeTab === 'reminders' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <h3 className="text-2xl font-bold text-gray-900">Event Reminders Status</h3>
                      <div className="flex gap-4 text-sm">
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                          Sent: {analytics.remindersAnalytics.sent}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                          Scheduled: {analytics.remindersAnalytics.scheduled}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                          Pending: {analytics.remindersAnalytics.pending}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                          Failed: {analytics.remindersAnalytics.failed}
                        </span>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reminder Time</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auto</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {analytics.remindersAnalytics.details.slice(0, 50).map((reminder) => (
                            <tr key={reminder.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900">{reminder.eventTitle}</div>
                                <div className="text-xs text-gray-500">{reminder.caseNo}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{reminder.recipient.name}</div>
                                <div className="text-xs text-gray-500">{reminder.recipient.email}</div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(reminder.status)}`}>
                                  {reminder.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {formatDate(reminder.reminderTime)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {reminder.autoScheduled ? '✓' : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* User Failures Tab */}
                {activeTab === 'failures' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">Users with Failed Notifications</h3>
                      <div className="relative w-96">
                        <input
                          type="text"
                          placeholder="Search by name, email, or user ID..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {searchQuery && (
                      <div className="text-sm text-gray-600 mb-2">
                        Found {filteredUserFailures.length} result(s) for "{searchQuery}"
                      </div>
                    )}
                    {filteredUserFailures.length > 0 ? (
                      <div className="space-y-4">
                        {filteredUserFailures.map((userFailure) => (
                          <div key={userFailure.user.userId} className="border border-red-200 rounded-lg p-6 bg-red-50">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">{userFailure.user.name}</h4>
                                <p className="text-sm text-gray-600">{userFailure.user.email}</p>
                                <p className="text-xs text-gray-500">User ID: {userFailure.user.userId}</p>
                                <p className="text-xs text-gray-500">Phone: {userFailure.user.mobileNumber}</p>
                              </div>
                              <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                                {userFailure.count} failures
                              </span>
                            </div>
                            <div className="space-y-2">
                              <h5 className="font-semibold text-gray-700">Failure Details:</h5>
                              {userFailure.failures.map((failure, idx) => (
                                <div key={idx} className="bg-white rounded p-3 text-sm">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-700">{failure.eventType}</span>
                                    <span className="text-gray-500">{formatDate(failure.timestamp)}</span>
                                  </div>
                                  <p className="text-red-600 mt-1">❌ {failure.reason}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-lg">
                          {searchQuery
                            ? `No users found matching "${searchQuery}"`
                            : 'No failed notifications in the selected time range'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Users Without Tokens Tab */}
                {activeTab === 'users' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">Users Without FCM Tokens</h3>
                      <div className="relative w-96">
                        <input
                          type="text"
                          placeholder="Search by name, email, phone, or user ID..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {searchQuery && (
                      <div className="text-sm text-gray-600 mb-2">
                        Found {filteredUsersWithoutTokens.length} result(s) for "{searchQuery}"
                      </div>
                    )}
                    {filteredUsersWithoutTokens.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsersWithoutTokens.map((user) => (
                              <tr key={user.userId} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{user.mobileNumber}</td>
                                <td className="px-6 py-4 text-xs text-gray-400 font-mono">{user.userId}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-lg">
                          {searchQuery
                            ? `No users found matching "${searchQuery}"`
                            : 'All users have FCM tokens registered!'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Expired Tokens Tab */}
                {activeTab === 'expired' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">Users with Expired/Invalid FCM Tokens</h3>
                      <div className="relative w-96">
                        <input
                          type="text"
                          placeholder="Search by name, email, or user ID..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {searchQuery && (
                      <div className="text-sm text-gray-600 mb-2">
                        Found {filteredUsersWithExpiredTokens.length} result(s) for "{searchQuery}"
                      </div>
                    )}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-800">
                        <strong>ℹ️ Note:</strong> These users have tokens that FCM rejected as invalid or expired. They need to log in again to generate fresh tokens.
                      </p>
                    </div>
                    {filteredUsersWithExpiredTokens.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token Count</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsersWithExpiredTokens.map((user) => (
                              <tr key={user.userId} className="hover:bg-gray-50">
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{user.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{user.mobileNumber}</td>
                                <td className="px-6 py-4 text-sm text-gray-500">{user.fcmTokenCount || 0}</td>
                                <td className="px-6 py-4 text-xs text-gray-400 font-mono">{user.userId}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-lg">
                          {searchQuery
                            ? `No users found matching "${searchQuery}"`
                            : 'No users with expired tokens!'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Excessive Tokens Tab */}
                {activeTab === 'excessive' && (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">Users with Excessive FCM Tokens</h3>
                      <div className="relative w-96">
                        <input
                          type="text"
                          placeholder="Search by name, email, or user ID..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {searchQuery && (
                      <div className="text-sm text-gray-600 mb-2">
                        Found {filteredUsersWithExcessiveTokens.length} result(s) for "{searchQuery}"
                      </div>
                    )}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-orange-800">
                        <strong>⚠️ Warning:</strong> These users have more than 5 FCM tokens. This usually happens when tokens aren't properly managed. Consider running the cleanup function.
                      </p>
                    </div>
                    {filteredUsersWithExcessiveTokens.length > 0 ? (
                      <div className="space-y-4">
                        {filteredUsersWithExcessiveTokens.map((user) => (
                          <div key={user.userId} className="border border-orange-200 rounded-lg p-6 bg-orange-50">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">{user.name}</h4>
                                <p className="text-sm text-gray-600">{user.email}</p>
                                <p className="text-xs text-gray-500">User ID: {user.userId}</p>
                                <p className="text-xs text-gray-500">Phone: {user.mobileNumber}</p>
                              </div>
                              <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                                {user.tokenCount} tokens
                              </span>
                            </div>
                            <div className="bg-white rounded p-3 text-sm">
                              <p className="text-gray-700">
                                <strong>Recommendation:</strong> This user should be cleaned up to max 5 tokens. Use the "Cleanup User Tokens" function in System Actions.
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-lg">
                          {searchQuery
                            ? `No users found matching "${searchQuery}"`
                            : 'All users have a reasonable number of tokens!'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Subscriptions Tab */}
                {activeTab === 'subscriptions' && (
                  <div className="space-y-6">
                    <SubscriptionManager />
                  </div>
                )}

                {/* Razorpay Transactions Tab */}
                {activeTab === 'razorpay' && (
                  <div className="space-y-6">
                    {/* Stats Cards */}
                    {razorpayStats && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-600 font-medium">Total</p>
                          <p className="text-2xl font-bold text-blue-800">{razorpayStats.total}</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm text-green-600 font-medium">Success</p>
                          <p className="text-2xl font-bold text-green-800">{razorpayStats.success}</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm text-red-600 font-medium">Failed</p>
                          <p className="text-2xl font-bold text-red-800">{razorpayStats.failed}</p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-sm text-yellow-600 font-medium">Cancelled</p>
                          <p className="text-2xl font-bold text-yellow-800">{razorpayStats.cancelled}</p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <p className="text-sm text-purple-600 font-medium">Total Revenue</p>
                          <p className="text-2xl font-bold text-purple-800">₹{razorpayStats.totalAmount?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    )}

                    {/* Filters and Search */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                      <div className="flex items-center gap-4">
                        <h3 className="text-2xl font-bold text-gray-900">Razorpay Transactions</h3>
                        <button
                          onClick={fetchRazorpayTransactions}
                          disabled={isLoadingRazorpay}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Refresh"
                        >
                          <svg className={`w-5 h-5 ${isLoadingRazorpay ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>
                      <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                        <SelectMenu
                          value={razorpayStatusFilter}
                          onChange={(e) => {
                            setRazorpayStatusFilter(e.target.value);
                            setRazorpayPagination(prev => ({ ...prev, page: 1 }));
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="all">All Status</option>
                          <option value="success">Success</option>
                          <option value="failed">Failed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="created">Pending</option>
                        </SelectMenu>
                        <div className="relative w-full md:w-80">
                          <input
                            type="text"
                            placeholder="Search by name, email, order ID..."
                            value={razorpaySearchQuery}
                            onChange={(e) => setRazorpaySearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                          <svg
                            className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Loading state */}
                    {isLoadingRazorpay ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : filteredRazorpayTransactions.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment ID</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredRazorpayTransactions.map((tx) => (
                                <tr key={tx.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                                    {tx.createdAt ? new Date(tx.createdAt).toLocaleString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    }) : 'N/A'}
                                  </td>
                                  <td className="px-4 py-4">
                                    <div className="text-sm font-medium text-gray-900">{tx.user?.name || 'Unknown'}</div>
                                    <div className="text-xs text-gray-500">{tx.user?.email || ''}</div>
                                    <div className="text-xs text-gray-400">{tx.user?.phone || ''}</div>
                                  </td>
                                  <td className="px-4 py-4">
                                    <div className="text-sm font-medium text-gray-900 capitalize">{tx.category || '-'}</div>
                                    <div className="text-xs text-gray-500">{tx.planType || ''}</div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {tx.currency === 'USD' ? '$' : '₹'}{tx.amountInRupees?.toLocaleString() || tx.amount / 100 || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">{tx.currency || 'INR'}</div>
                                  </td>
                                  <td className="px-4 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      tx.status === 'success' ? 'bg-green-100 text-green-800' :
                                      tx.status === 'failed' ? 'bg-red-100 text-red-800' :
                                      tx.status === 'cancelled' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {tx.status || 'unknown'}
                                    </span>
                                    {tx.errorMessage && (
                                      <div className="text-xs text-red-500 mt-1 max-w-xs truncate" title={tx.errorMessage}>
                                        {tx.errorMessage}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-4 text-xs text-gray-500 font-mono">
                                    {tx.orderId || '-'}
                                  </td>
                                  <td className="px-4 py-4 text-xs text-gray-500 font-mono">
                                    {tx.paymentId || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {razorpayPagination.totalPages > 1 && (
                          <div className="flex justify-between items-center mt-4">
                            <p className="text-sm text-gray-600">
                              Showing page {razorpayPagination.page} of {razorpayPagination.totalPages} ({razorpayPagination.totalCount} total)
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setRazorpayPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={razorpayPagination.page <= 1}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => setRazorpayPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={razorpayPagination.page >= razorpayPagination.totalPages}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-lg">
                          {razorpaySearchQuery
                            ? `No transactions found matching "${razorpaySearchQuery}"`
                            : 'No Razorpay transactions found'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Admin Actions */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-6">⚙️ System Actions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {/* Clear Past Hearing Dates Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-3 rounded-full">
                    <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white">Clear Past Hearing Dates</h2>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  This will clear the <code className="bg-gray-100 px-2 py-1 rounded text-sm">nextHearingDate</code> field for all cases in <strong>pending</strong> and <strong>disposed</strong> collections where the hearing date is in the past (excluding today).
                </p>

                <button
                  onClick={() => openConfirmModal(handleClearPastHearingDates)}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Clear Past Hearing Dates
                </button>
              </div>
            </div>

            {/* Send App Update Notification Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-3 rounded-full">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white">Send Update Notification</h2>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Send push notification to all users asking them to update the app.
                </p>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Notification Title
                    </label>
                    <input
                      type="text"
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Enter notification title"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Notification Message
                    </label>
                    <textarea
                      value={notificationMessage}
                      onChange={(e) => setNotificationMessage(e.target.value)}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                      placeholder="Enter notification message"
                    />
                  </div>
                </div>

                <button
                  onClick={() => openConfirmModal(handleSendAppUpdateNotification)}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send to All Users
                </button>
              </div>
            </div>

            {/* Cleanup FCM Tokens Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-purple-500 to-pink-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-3 rounded-full">
                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white">Cleanup User Tokens</h2>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Clean up excessive FCM tokens for all users. This will keep only the <strong>5 most recent tokens</strong> per user and remove duplicates, improving notification efficiency.
                </p>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>ℹ️ Note:</strong> This will automatically remove expired tokens and limit each user to 5 devices maximum.
                  </p>
                </div>

                <button
                  onClick={() => openConfirmModal(async () => {
                    setIsLoading(true);
                    setResultMessage('');
                    try {
                      const cleanupFunction = httpsCallable(functions, 'cleanupUserTokens');
                      const result = await cleanupFunction({ maxTokensPerUser: 5 });
                      const data = result.data;

                      let resultText = `✅ ${data.message}\n\n`;
                      resultText += `📊 Statistics:\n`;
                      resultText += `• Users Processed: ${data.usersProcessed}\n`;
                      resultText += `• Total Tokens Removed: ${data.totalTokensRemoved}\n`;
                      resultText += `• Max Tokens Per User: ${data.maxTokensPerUser}\n`;

                      setResultMessage(resultText);
                      toast.success('Token cleanup completed successfully!');
                      fetchAnalytics(); // Refresh analytics
                    } catch (error) {
                      console.error('Error cleaning up tokens:', error);
                      setResultMessage(`❌ Error: ${error.message}`);
                      toast.error(`Failed to cleanup tokens: ${error.message}`);
                    } finally {
                      setIsLoading(false);
                      setShowConfirmModal(false);
                    }
                  })}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Cleanup All User Tokens
                </button>
              </div>
            </div>

            {/* Extend Free Subscriptions Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="bg-gradient-to-r from-green-500 to-teal-600 p-6">
                <div className="flex items-center gap-3">
                  <div className="bg-white p-3 rounded-full">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-white">Extend Free Subscriptions</h2>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Extend all users' <strong>free subscriptions</strong> by one week (7 days). This will send a professional notification to all users informing them about the subscription extension.
                </p>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>ℹ️ Info:</strong> Only free subscriptions will be extended. Paid subscriptions will not be affected. All users will receive a beautiful FCM notification.
                  </p>
                </div>

                <button
                  onClick={() => openConfirmModal(handleExtendFreeSubscriptions)}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-green-500 to-teal-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-teal-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Extend Subscriptions by 1 Week
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Result Display */}
        {resultMessage && (
          <div className="bg-white rounded-xl shadow-lg p-6 animate-fade-in mb-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-green-100 p-2 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Operation Result</h3>
                <div className="border-t border-gray-200 pt-4">
                  <pre className="text-gray-700 whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-lg overflow-x-auto">
                    {resultMessage}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Information Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-bold text-blue-900 mb-2">Important Information</h4>
              <ul className="text-blue-800 space-y-1 text-sm">
                <li>• Analytics data updates in real-time and tracks all notification events</li>
                <li>• Failed notifications show detailed user information and error reasons</li>
                <li>• Users without FCM tokens cannot receive push notifications</li>
                <li>• Event reminders are automatically scheduled based on hearing dates</li>
                <li>• All operations are logged in Firebase Functions logs</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminFunctions;
